/**
 * Keeping the popup host the size of the window when a store zooms its page.
 *
 * Some themes shrink the whole storefront with CSS zoom: Health Matrix sets
 * `body { zoom: 0.9 }` (29 Oct 2026). Zoom multiplies down the tree, so the
 * host, a child of <body>, measured 100vw x 100vh and was then painted at 90%:
 * 1296 x 738 on a 1440 x 820 window. Its `contain: paint` clipped the backdrop
 * to that box, so the page showed through along the right and bottom edges.
 *
 * The host cancels whatever zoom its ancestors apply. Inside it one CSS pixel
 * is one screen pixel again: the backdrop covers the window and the popup
 * renders at the size it was designed at, whatever the theme does to its page.
 */

type ZoomedElement = Element & { currentCSSZoom?: number };

/** The zoom an element is painted at: its own zoom times every ancestor's. */
export function pageZoomAt(el: Element | null): number {
  if (!el) return 1;
  // Chrome 128+, Firefox 126+ and Safari 18.2+ report the product directly.
  const current = (el as ZoomedElement).currentCSSZoom;
  if (typeof current === "number" && current > 0) return current;
  let zoom = 1;
  for (let node: Element | null = el; node; node = node.parentElement) {
    const own = parseFloat(window.getComputedStyle(node).zoom);
    if (own > 0) zoom *= own;
  }
  return zoom;
}

/** The zoom the host needs so its ancestors' zoom multiplies back to 1. */
export function hostZoomFor(parent: Element | null): string {
  const zoom = pageZoomAt(parent);
  if (!(zoom > 0) || Math.abs(zoom - 1) < 0.001) return "1";
  return String(1 / zoom);
}

/** Re-read the page's zoom (a theme can change it at a breakpoint or after
 *  its stylesheet lands) and set the host's to cancel it. */
export function matchHostToWindow(host: HTMLElement): void {
  const next = hostZoomFor(host.parentElement);
  if (host.style.getPropertyValue("zoom") === next) return;
  host.style.setProperty("zoom", next, "important");
}
